import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Link, useHistory, useLocation } from 'react-router-dom'
import qs from 'query-string'
import range from 'lodash.range'
import { FadeVisual } from 'components/misc/Fade'
import usePrevious from 'hooks/usePrevious'

import styles from './styles/Paginator.css'

const _getPageUrl = (page, { location, query, queryKey }) => {
  const resultQuery = { ...query, [queryKey]: page }
  if (page === 1) {
    delete resultQuery[queryKey]
  }
  return location.pathname + '?' + qs.stringify(resultQuery)
}

/* ensure the previously selected page isn't highlighted with focus state during re-render. */
const _blurOnClick = () => el => el && el.blur()

const getDisplayValues = ({ pageCount, currentPage, pageNavLength }) => {
  const contractPages = pageCount > pageNavLength
  const values = range(pageNavLength).map(n => n + 1)

  if (!contractPages) return values.slice(0, pageCount)

  if (currentPage < pageNavLength) {
    return values
  }

  if (currentPage >= pageCount - pageNavLength) {
    return values.map(n => n + (pageCount - pageNavLength))
  }

  const halfPageNavLength = Math.ceil(pageNavLength / 2)
  return values.map(n => n + (currentPage - halfPageNavLength))
}

const Arrows = ({
  flipped,
  double,
  nextPage,
  endPage,
  useAnchorTag = true
}) => {
  const history = useHistory()
  const layout = classNames(styles.arrows, {
    [styles.flipped]: flipped
  })
  const doubleStyle = flipped ? styles.doubleArrowRight : styles.doubleArrowLeft
  const singleStyle = flipped ? styles.arrowRight : styles.arrowLeft

  const doubleArrow = useAnchorTag
    ? <Link className={doubleStyle} to={endPage} innerRef={_blurOnClick()} />
    : <i className={doubleStyle} onClick={() => history.push(endPage)} tabIndex='0' ref={_blurOnClick()} />
  const singleArrow = useAnchorTag
    ? <Link className={singleStyle} to={nextPage} innerRef={_blurOnClick()} />
    : <i className={singleStyle} onClick={() => history.push(nextPage)} tabIndex='0' ref={_blurOnClick()} />

  return (
    <div className={layout}>
      {double && doubleArrow}
      {singleArrow}
    </div>
  )
}

const PageNavigationItem = ({
  count,
  url,
  currentPage,
  useAnchorTag = true
}) => {
  const history = useHistory()
  const isCurrentPage = currentPage === count

  if (isCurrentPage) {
    return (
      <span className={classNames(styles.navItem, styles.numberSelected)} data-testid='page-number'>
        {count}
      </span>
    )
  }

  if (useAnchorTag) {
    return (
      <Link className={styles.navItem} to={url} innerRef={_blurOnClick()} data-testid='page-number'>
        {count}
      </Link>
    )
  }

  return (
    <span
      className={styles.navItem}
      data-testid='page-number'
      onClick={() => history.push(url)}
      tabIndex={0}
      ref={_blurOnClick()}
    >
      {count}
    </span>
  )
}

export const Paginator = ({
  className,
  pageCount,
  currentPage,
  pageNavLength = 10,
  queryKey = 'page',
  scrollToRef = null,
  linkToEnd = false,
  useAnchorTag = true,
  noScroll = false
}) => {
  const location = useLocation()
  const query = qs.parse(location.search)

  const prevQueryVal = usePrevious(query[queryKey])
  const [initialRender, setInitialRender] = useState(true)

  const queryNoPage = { ...query }
  delete queryNoPage[queryKey]

  const firstPage = _getPageUrl(1, { location, query, queryKey })
  const prevPage = _getPageUrl(currentPage - 1, { location, query, queryKey })
  const nextPage = _getPageUrl(currentPage + 1, { location, query, queryKey })
  const lastPage = _getPageUrl(pageCount, { location, query, queryKey })

  useEffect(() => {
    setInitialRender(false)
  }, [])

  useEffect(() => {
    if (noScroll || initialRender) {
      return
    }

    if (prevQueryVal === query[queryKey]) {
      return
    }

    if (scrollToRef) {
      scrollToRef.current.scrollIntoView()
    } else {
      window.scrollTo(0, 0)
    }
  }, [query[queryKey], noScroll, initialRender])

  return (
    <div className={classNames(styles.paginator, className)}>
      <FadeVisual className={styles.leftArrowWrapper} show={currentPage > 1}>
        <Arrows
          double={pageCount > 5}
          nextPage={prevPage}
          endPage={firstPage}
          useAnchorTag={useAnchorTag}
        />
      </FadeVisual>

      {
        getDisplayValues({ pageCount, currentPage, pageNavLength }).map((count, index) => (
          <PageNavigationItem
            key={index}
            count={count}
            url={_getPageUrl(count, { location, query, queryKey })}
            currentPage={currentPage}
            queryKey={queryKey}
            useAnchorTag={useAnchorTag}
          />
        ))
      }

      <FadeVisual className={styles.rightArrowWrapper} show={currentPage < pageCount}>
        <Arrows
          flipped
          double={linkToEnd && pageCount > 5}
          nextPage={nextPage}
          endPage={lastPage}
          useAnchorTag={useAnchorTag}
        />
      </FadeVisual>
    </div>
  )
}

export default Paginator
